import { useState, useCallback, useEffect } from 'react';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient, jobService, favouriteJobsService } from '../services/api';
import { JobStatus, JobType } from '../types';
import type { JobSummaryDto, FavouriteJobSummary, AvailableJobsResponse } from '../types';

const JOB_TYPE_LABELS: Record<number, string> = {
  [JobType.SameDayDelivery]: 'Same Day',
  [JobType.ScheduledDelivery]: 'Scheduled',
  [JobType.RelayDelivery]: 'Relay',
  [JobType.VehicleRepair]: 'Repair',
  [JobType.Removal]: 'Removal',
  [JobType.FreightHaulage]: 'Freight',
};

const STATUS_SECTIONS = [
  {
    label: 'Pending Review',
    statuses: [JobStatus.PendingReview],
    color: '#92400e', bg: '#fef3c7', dot: '#f59e0b',
    description: 'Awaiting admin approval',
  },
  {
    label: 'Open',
    statuses: [JobStatus.Active, JobStatus.Quoted],
    color: '#065f46', bg: '#d1fae5', dot: '#34d399',
    description: 'Accepting quotes',
  },
  {
    label: 'Active',
    statuses: [JobStatus.Accepted, JobStatus.ContractGenerated, JobStatus.CarrierAccepted, JobStatus.InProgress],
    color: '#166534', bg: '#bbf7d0', dot: '#15803d',
    description: 'Awarded & underway',
  },
  {
    label: 'Delivered',
    statuses: [JobStatus.Completed],
    color: '#0f766e', bg: '#ccfbf1', dot: '#0d9488',
    description: 'Awaiting proof of delivery',
  },
  {
    label: 'Delivered — Verified',
    statuses: [JobStatus.PodVerified],
    color: '#134e4a', bg: '#99f6e4', dot: '#0f766e',
    description: 'POD confirmed',
  },
  {
    label: 'Completed',
    statuses: [JobStatus.PaymentReleased, JobStatus.ReviewUnlocked],
    color: '#14532d', bg: '#dcfce7', dot: '#22c55e',
    description: 'Payment released',
  },
  {
    label: 'Cancelled',
    statuses: [JobStatus.Cancelled, JobStatus.Rejected],
    color: '#991b1b', bg: '#fee2e2', dot: '#ef4444',
    description: '',
  },
];

interface ChipDef {
  key: string;
  label: string;
  icon: string;
  color: string;
  bg: string;
  statuses: JobStatus[] | null;
  isSpecial?: boolean;
}

const CHIP_DEFS: ChipDef[] = [
  { key: 'all',        icon: '📋', label: 'Total',          color: '#374151', bg: '#f3f4f6', statuses: null },
  { key: 'available',  icon: '⭐', label: 'Available',       color: '#1d4ed8', bg: '#dbeafe', statuses: null, isSpecial: true },
  { key: 'active',     icon: '🔄', label: 'Active',          color: '#166534', bg: '#bbf7d0', statuses: [JobStatus.Active, JobStatus.Quoted, JobStatus.Accepted, JobStatus.ContractGenerated, JobStatus.CarrierAccepted, JobStatus.InProgress] },
  { key: 'pendingRev', icon: '⏳', label: 'Pending Review',  color: '#92400e', bg: '#fef3c7', statuses: [JobStatus.PendingReview] },
  { key: 'pendingPod', icon: '🚚', label: 'Delivered',       color: '#0f766e', bg: '#ccfbf1', statuses: [JobStatus.Completed, JobStatus.PodVerified] },
  { key: 'complete',   icon: '✅', label: 'Completed',       color: '#14532d', bg: '#dcfce7', statuses: [JobStatus.PaymentReleased, JobStatus.ReviewUnlocked] },
  { key: 'cancelled',  icon: '❌', label: 'Cancelled',       color: '#991b1b', bg: '#fee2e2', statuses: [JobStatus.Cancelled, JobStatus.Rejected] },
];

function truncate(s: string, n = 28) {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function StarBtn({ jobId, isFavourited, onToggle }: {
  jobId: string;
  isFavourited?: boolean;
  onToggle: (jobId: string, current: boolean) => void;
}) {
  return (
    <button
      onClick={e => { e.preventDefault(); e.stopPropagation(); onToggle(jobId, !!isFavourited); }}
      title={isFavourited ? 'Remove from favourites' : 'Add to favourites'}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 15, padding: '0 4px', lineHeight: 1,
        color: isFavourited ? '#f59e0b' : '#d1d5db',
        transition: 'color 0.15s',
      }}
    >
      {isFavourited ? '★' : '☆'}
    </button>
  );
}

function StatCard({ chip, value, selected, onClick }: {
  chip: ChipDef;
  value: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...s.statCard,
        background: chip.bg,
        border: selected ? `2px solid ${chip.color}` : `1.5px solid ${chip.bg}`,
        boxShadow: selected ? `0 0 0 3px ${chip.bg}, 0 2px 8px rgba(0,0,0,0.1)` : '0 1px 6px rgba(0,0,0,0.06)',
        transform: selected ? 'scale(1.03)' : 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        outline: 'none',
        textAlign: 'center',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
      }}
    >
      <span style={{ fontSize: 22 }}>{chip.icon}</span>
      {!chip.isSpecial && (
        <span style={{ fontSize: 20, fontWeight: 800, color: chip.color }}>{value}</span>
      )}
      <span style={{ fontSize: 11, color: chip.color, opacity: 0.85 }}>{chip.label}</span>
      <span style={{ fontSize: 9, color: chip.color, opacity: 0.65, fontWeight: 700, marginTop: 2 }}>
        {chip.isSpecial ? (value > 0 ? `${value} matched` : 'No matches') : selected ? '▲ Clear' : '▼ Filter'}
      </span>
    </button>
  );
}

function AvailableJobsPanel({
  available,
  onToggleFavourite,
}: {
  available: AvailableJobsResponse | null;
  onToggleFavourite: (jobId: string, isFav: boolean) => void;
}) {
  const jobs = available?.jobs ?? [];
  const total = available?.totalCount ?? 0;

  if (total === 0) {
    return (
      <div style={s.empty}>
        <span style={{ fontSize: 40 }}>⭐</span>
        <p style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: '12px 0 4px' }}>
          No active jobs in your area right now
        </p>
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
          Check back soon, or{' '}
          <a href="/job-alerts" style={{ color: '#1d4ed8', fontWeight: 600 }}>set up job alerts</a>
          {' '}to get notified of new matches.
        </p>
      </div>
    );
  }

  return (
    <div style={s.jobListScroll}>
      <div style={{ ...s.section, borderLeft: '4px solid #1d4ed8' }}>
        <div style={s.sectionHead}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ ...s.sectionBadge, background: '#dbeafe', color: '#1d4ed8' }}>
              ⭐ Available Jobs
            </span>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              {total} job{total !== 1 ? 's' : ''} active in your area
            </span>
          </div>
        </div>
        <div style={s.tableWrap}>
          <div style={s.tableHead}>
            <span style={{ width: 100 }}>Reference</span>
            <span style={{ flex: 1 }}>Title</span>
            <span style={{ flex: 1.4 }}>Route</span>
            <span style={{ width: 80 }}>Type</span>
            <span style={{ width: 90 }}>Date</span>
            <span style={{ width: 60, textAlign: 'right' }}>Quotes</span>
            <span style={{ width: 30 }}></span>
          </div>
          {jobs.map(job => (
            <a key={job.jobId} href={`/jobs/${job.jobId}`} style={s.jobRow}
              onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ width: 100, fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                {job.referenceNumber}
              </span>
              <span style={{ flex: 1, fontWeight: 600, color: '#111827', fontSize: 13 }}>
                {truncate(job.title, 32)}
              </span>
              <span style={{ flex: 1.4, fontSize: 11, color: '#6b7280' }}>
                {truncate(job.pickupAddress, 18)} → {truncate(job.deliveryAddress, 18)}
              </span>
              <span style={{ width: 80, fontSize: 11, color: '#374151' }}>
                {JOB_TYPE_LABELS[job.jobType] ?? '—'}
              </span>
              <span style={{ width: 90, fontSize: 11, color: '#6b7280' }}>
                {new Date(job.scheduledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
              </span>
              <span style={{ width: 60, textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                {job.quoteCount}
              </span>
              <span style={{ width: 30, textAlign: 'right' }}>
                <StarBtn jobId={job.jobId} isFavourited={job.isFavourited} onToggle={onToggleFavourite} />
              </span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function JobsBoardPage() {
  const { getToken, account } = useAppAuth();
  const { isAdminOrOps, isCarrierOrWorkshop, isRoleLoading } = useRole();
  const [jobs, setJobs]                   = useState<JobSummaryDto[]>([]);
  const [available, setAvailable]         = useState<AvailableJobsResponse | null>(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [activeChipKey, setActiveChipKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!account || isRoleLoading) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const client = createApiClient(getToken);
      const svc    = jobService(client);
      const favSvc = favouriteJobsService(client);

      const [data, avail] = await Promise.all([
        isAdminOrOps ? svc.getAllJobs() : svc.getMyJobs(),
        isCarrierOrWorkshop ? favSvc.getAvailable().catch(() => null) : Promise.resolve(null),
      ]);

      setJobs(data);
      setAvailable(avail);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [account, getToken, isAdminOrOps, isCarrierOrWorkshop, isRoleLoading]);

  useEffect(() => { load(); }, [load]);

  const handleToggleFavourite = useCallback(async (jobId: string, isCurrent: boolean) => {
    try {
      const client = createApiClient(getToken);
      const favSvc = favouriteJobsService(client);
      if (isCurrent) {
        await favSvc.remove(jobId);
      } else {
        await favSvc.add(jobId);
      }
      // Refresh jobs and available list to reflect updated isFavourited flags
      const [updatedJobs, updatedAvail] = await Promise.all([
        isAdminOrOps ? jobService(createApiClient(getToken)).getAllJobs() : jobService(createApiClient(getToken)).getMyJobs(),
        isCarrierOrWorkshop ? favSvc.getAvailable().catch(() => null) : Promise.resolve(null),
      ]);
      setJobs(updatedJobs);
      setAvailable(updatedAvail);
    } catch {
      // Silently ignore — user can retry
    }
  }, [getToken, isAdminOrOps, isCarrierOrWorkshop]);

  if (loading || isRoleLoading) return <div style={s.loading}><div style={s.spinner} /></div>;
  if (error)                    return <div style={s.loading}><p style={{ color: '#dc2626' }}>⚠ {error}</p></div>;

  const availableCount = available?.totalCount ?? 0;

  const chipValues: Record<string, number> = {
    all:        jobs.length,
    available:  availableCount,
    active:     jobs.filter(j => [JobStatus.Active, JobStatus.Quoted, JobStatus.Accepted, JobStatus.ContractGenerated, JobStatus.CarrierAccepted, JobStatus.InProgress].includes(j.status)).length,
    pendingRev: jobs.filter(j => j.status === JobStatus.PendingReview).length,
    pendingPod: jobs.filter(j => j.status === JobStatus.Completed || j.status === JobStatus.PodVerified).length,
    complete:   jobs.filter(j => j.status === JobStatus.PaymentReleased || j.status === JobStatus.ReviewUnlocked).length,
    cancelled:  jobs.filter(j => j.status === JobStatus.Cancelled || j.status === JobStatus.Rejected).length,
  };

  // Only show Available chip for carriers/workshops (not admin/ops/customer)
  const visibleChips = isCarrierOrWorkshop
    ? CHIP_DEFS
    : CHIP_DEFS.filter(c => c.key !== 'available');

  const activeChip = CHIP_DEFS.find(c => c.key === activeChipKey) ?? null;

  const visibleSections = activeChip?.statuses
    ? STATUS_SECTIONS.filter(s => s.statuses.some(st => activeChip.statuses!.includes(st)))
    : STATUS_SECTIONS;

  function handleChipClick(key: string) {
    setActiveChipKey(prev => (prev === key ? null : key));
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>{isAdminOrOps ? 'All Jobs (Admin)' : 'Jobs Board'}</h1>
          <p style={s.subtitle}>
            {isAdminOrOps
              ? 'Platform-wide view of all jobs across all users'
              : 'Click a status chip to filter — click a job row to view details'}
          </p>
        </div>
        <button onClick={load} style={s.refreshBtn}>↻ Refresh</button>
      </div>

      {/* Stat cards — clickable filters */}
      <div style={s.statsGrid}>
        {visibleChips.map(chip => (
          <StatCard
            key={chip.key}
            chip={chip}
            value={chipValues[chip.key] ?? 0}
            selected={chip.key === activeChipKey}
            onClick={() => handleChipClick(chip.key)}
          />
        ))}
      </div>

      {/* Available Jobs panel */}
      {activeChipKey === 'available' && (
        <AvailableJobsPanel available={available} onToggleFavourite={handleToggleFavourite} />
      )}

      {/* Active filter header for non-available chips */}
      {activeChip && activeChipKey !== 'available' && (
        <div style={s.filterBanner}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 4, height: 20, borderRadius: 2, background: activeChip.color }} />
            <strong style={{ color: activeChip.color }}>{activeChip.label}</strong>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              · {chipValues[activeChip.key]} job{chipValues[activeChip.key] !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={() => setActiveChipKey(null)}
            style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Clear ×
          </button>
        </div>
      )}

      {/* Job list — rendered when a non-available card is selected */}
      {activeChip && activeChipKey !== 'available' ? (
        <div style={s.jobListScroll}>
          {visibleSections.map(section => {
            const sectionJobs = jobs.filter(j => (section.statuses as JobStatus[]).includes(j.status));
            if (sectionJobs.length === 0) return null;
            return (
              <div key={section.label} style={{ ...s.section, borderLeft: `4px solid ${section.dot}` }}>
                <div style={s.sectionHead}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ ...s.sectionBadge, background: section.bg, color: section.color }}>
                      {section.label}
                    </span>
                    {section.description && (
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>{section.description}</span>
                    )}
                  </div>
                  <span style={s.sectionCount}>{sectionJobs.length} job{sectionJobs.length !== 1 ? 's' : ''}</span>
                </div>

                <div style={s.tableWrap}>
                  <div style={s.tableHead}>
                    <span style={{ width: 100 }}>Reference</span>
                    <span style={{ flex: 1 }}>Title</span>
                    <span style={{ flex: 1.4 }}>Route</span>
                    <span style={{ width: 80 }}>Type</span>
                    <span style={{ width: 90 }}>Date</span>
                    <span style={{ width: 60, textAlign: 'right' }}>Quotes</span>
                    {isCarrierOrWorkshop && <span style={{ width: 30 }}></span>}
                  </div>
                  {sectionJobs.map(job => (
                    <a key={job.id} href={`/jobs/${job.id}`} style={s.jobRow}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ width: 100, fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                        {job.referenceNumber}
                      </span>
                      <span style={{ flex: 1, fontWeight: 600, color: '#111827', fontSize: 13 }}>
                        {truncate(job.title, 32)}
                      </span>
                      <span style={{ flex: 1.4, fontSize: 11, color: '#6b7280' }}>
                        {truncate(job.pickupAddress, 18)} → {truncate(job.deliveryAddress, 18)}
                      </span>
                      <span style={{ width: 80, fontSize: 11, color: '#374151' }}>
                        {JOB_TYPE_LABELS[job.jobType] ?? '—'}
                      </span>
                      <span style={{ width: 90, fontSize: 11, color: '#6b7280' }}>
                        {job.scheduledAt
                          ? new Date(job.scheduledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                          : '—'}
                      </span>
                      <span style={{ width: 60, textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#374151' }}>
                        {job.quoteCount}
                      </span>
                      {isCarrierOrWorkshop && job.status === JobStatus.Active && (
                        <span style={{ width: 30, textAlign: 'right' }}>
                          <StarBtn jobId={job.id} isFavourited={job.isFavourited} onToggle={handleToggleFavourite} />
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            );
          })}

          {/* No results for selected filter */}
          {visibleSections.every(sec => jobs.filter(j => sec.statuses.includes(j.status)).length === 0) && (
            <div style={s.empty}>
              <span style={{ fontSize: 40 }}>🔍</span>
              <p style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: '12px 0 4px' }}>
                No {activeChip.label} jobs
              </p>
              <button
                onClick={() => setActiveChipKey(null)}
                style={{ marginTop: 12, padding: '8px 20px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Clear filter →
              </button>
            </div>
          )}
        </div>
      ) : activeChipKey === null && (
        /* No card selected — prompt */
        <div style={s.empty}>
          <span style={{ fontSize: 48 }}>📊</span>
          <p style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: '12px 0 4px' }}>
            Select a status to view jobs
          </p>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
            Click any card above — Total shows all {jobs.length} job{jobs.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {jobs.length === 0 && activeChipKey === null && (
        <div style={s.empty}>
          <span style={{ fontSize: 48 }}>📭</span>
          <p style={{ fontSize: 16, fontWeight: 700, color: '#374151', margin: '12px 0 4px' }}>No jobs found</p>
          {isAdminOrOps ? (
            <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No jobs exist on the platform yet.</p>
          ) : (
            <>
              <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Post your first job to get started.</p>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <a href="/post-job"    style={s.cta}>Post Delivery →</a>
                <a href="/post-repair" style={{ ...s.cta, background: '#6d28d9' }}>Post Repair →</a>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:         { width: '100%', padding: '24px 16px' },
  loading:      { minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner:      { width: 36, height: 36, border: '3px solid #e5e7eb', borderTop: '3px solid #047857', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  h1:           { fontSize: 24, fontWeight: 800, color: '#111827', margin: 0 },
  subtitle:     { fontSize: 13, color: '#6b7280', marginTop: 4, margin: '4px 0 0' },
  refreshBtn:   { padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13 },
  statsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 20 },
  statCard:     { borderRadius: 10, padding: '16px 12px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 4, transition: 'border-color 0.15s, box-shadow 0.15s' },
  filterBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: '10px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' },
  jobListScroll:{ maxHeight: 540, overflowY: 'auto' as const, paddingRight: 2 },
  section:      { background: '#fff', borderRadius: 12, padding: '18px 20px', marginBottom: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
  sectionHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  sectionBadge: { fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 },
  sectionCount: { fontSize: 12, color: '#9ca3af' },
  tableWrap:    { overflowX: 'auto' as const },
  tableHead:    { display: 'flex', gap: 8, padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  jobRow:       { display: 'flex', gap: 8, padding: '10px', borderRadius: 8, textDecoration: 'none', color: 'inherit', alignItems: 'center', transition: 'background 0.12s', cursor: 'pointer' },
  empty:        { textAlign: 'center' as const, padding: '60px 20px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center', background: '#fff', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
  cta:          { padding: '10px 20px', background: '#047857', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 },
};
